/**
 * Temporary screen-scoped design tokens for **Settings → Delegation**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Delegation" section) 1:1.
 * Every colour, size, radius, spacing and type value the screen needs lives
 * here — components must never inline a literal. Once all screens are
 * re-skinned the shared values move into `src/theme` and this file is removed.
 */

import { button, contentCard, formField, signer, switchControl } from "theme/colors";

export const delegationTokens = {
  /** White rounded card holding the whole screen. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    padding: "24px 28px",
    /** Gap between header block, field grid and the button row. */
    gap: "18px",
  },
  /** Card heading + supporting sentence, with the toggle pushed to the right. */
  header: {
    gap: "14px",
    textGap: "3px",
    titleColor: signer.ink,
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    descriptionColor: signer.text.meta,
    descriptionFontSize: "12.5px",
    descriptionLineHeight: 1.55,
  },
  /** Shared app toggle chrome — see `theme/colors.ts` `switchControl`. */
  toggle: {
    width: switchControl.width,
    height: switchControl.height,
    knobSize: switchControl.knobSize,
    knobTop: `${switchControl.knobInset}px`,
    knobOffsetOff: `translateX(${switchControl.knobInset}px)`,
    knobOffsetOn: `translateX(${switchControl.knobInset + switchControl.knobTravel}px)`,
    trackOff: switchControl.trackOff,
    trackOn: switchControl.trackOn,
    knobBg: switchControl.knob,
    knobShadow: switchControl.knobShadow,
    transition: switchControl.trackTransition,
    knobTransition: switchControl.knobTransition,
    /** Optically aligns the pill with the first line of the heading. */
    marginTop: "2px",
    /** Small padlock shown beside the toggle when the plan lacks the feature. */
    lockSize: 14,
    lockColor: signer.text.faint,
    lockGap: "6px",
  },
  field: {
    /*
      Field geometry is the app's, not this screen's: 44px on a 10px radius
      with 14px text and a 14px inset, matching Contacts and the shared
      `CustomInput` default. These four Settings screens had drifted to a
      40/9px/13px box of their own, which read as a different control from one
      page to the next. See `global/components/CustomInput/FormField.tokens.ts`.
    */
    rowGap: "14px",
    columnGap: "18px",
    labelGap: "6px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: signer.ink,
    height: 44,
    radius: "10px",
    border: formField.border,
    bg: formField.bg,
    color: signer.ink,
    fontSize: "14px",
    fontWeight: 500,
    /*
      15px, not 14: the design inset is measured inside the field's 1px border,
      but MUI paints that border on an overlaid `fieldset` rather than
      insetting it, so the input needs design padding + border width to land on
      the same pixel. Contacts resolves to the same 15px.
    */
    paddingX: "15px",
    focusBorder: signer.orange.main,
    placeholder: formField.placeholderMuted,
    errorFontSize: "10.5px",
    /** Whole grid dims while delegation is switched off. */
    disabledOpacity: 0.45,
    opacityTransition: "opacity .15s",
  },
  /** Read-only date-range trigger — same chrome as the inputs beside it. */
  dateRange: {
    gap: "9px",
    iconSize: 14,
    iconColor: signer.text.faint,
    iconStrokeWidth: 2,
  },
  /** Chrome mirrors the global `MuiButton` `contained` override — see `button`. */
  primaryButton: {
    ...button,
    lockSize: 14,
  },
  /** Placeholder blocks shown while the configuration loads. */
  skeleton: {
    titleWidth: 170,
    titleHeight: 18,
    descriptionHeight: 13,
    descriptionWidth: "72%",
    labelWidth: 96,
    labelHeight: 11,
    buttonWidth: 104,
  },
} as const;

/**
 * Phone (`xs`) and tablet (`sm`) values for **Settings → Delegation**, traced
 * from `Settings - Mobile & Tablet.dc.html`.
 *
 * Only what differs from the desktop tokens above lives here; anything omitted
 * is inherited at `md`, so the desktop screen is untouched. `sm` / `std` inside
 * `phone` are the reference's 375 and 390 columns.
 */
export const delegationResponsiveTokens = {
  phone: {
    card: {
      padding: { sm: "18px 16px", std: "20px 18px" },
      gap: { sm: "14px", std: "15px" },
    },
    header: {
      gap: "12px",
      titleFontSize: { sm: "14.5px", std: "15px" },
      descriptionFontSize: { sm: "12px", std: "12.5px" },
    },
    field: {
      rowGap: { sm: "14px", std: "15px" },
      labelFontSize: { sm: "11.5px", std: "12px" },
      height: { sm: 42, std: 44 },
      fontSize: { sm: "13.5px", std: "14px" },
    },
    primaryButton: {
      height: { sm: 42, std: 44 },
      paddingX: { sm: "20px", std: "22px" },
      fontSize: { sm: "13px", std: "13.5px" },
    },
  },

  tablet: {
    card: { padding: "22px 24px", gap: "16px" },
    header: { gap: "14px", titleFontSize: "14.5px", descriptionFontSize: "12px" },
    field: {
      rowGap: "14px",
      columnGap: "14px",
      labelFontSize: "11px",
      height: 38,
      radius: "8px",
      paddingX: "11px",
      fontSize: "13px",
    },
    primaryButton: {
      height: 38,
      paddingX: "18px",
      radius: "8px",
      fontSize: "12.5px",
    },
  },
} as const;

export default delegationTokens;
